import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, FileBarChart } from 'lucide-react';
import { Card, Select, TextView } from '@/shims/uiCommonComponents';
import type { IcDispute, Period } from '@/types';
import { periodDisplayName } from '@/types';
import { icDisputesApi, periodsApi } from '@/api/client';

export default function IcDisputes() {
  const [periods, setPeriods] = useState<Period[]>([]);
  const [disputes, setDisputes] = useState<IcDispute[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!selectedPeriodId) {
      setDisputes([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await icDisputesApi.list({
        period_id: Number(selectedPeriodId),
        page_size: 200,
      });
      setDisputes(res.items);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load disputes');
      setDisputes([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPeriodId]);

  useEffect(() => {
    const loadPeriods = async () => {
      try {
        const p = await periodsApi.list();
        setPeriods(p);
        const openPeriod = p.find((x) => x.status === 'open') || p[0];
        if (openPeriod) setSelectedPeriodId(String(openPeriod.id));
      } catch {
        setError('Failed to load periods');
      }
    };
    loadPeriods();
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      <div>
        <TextView variant="heading-1">IC Disputes</TextView>
        <TextView variant="body-sm">
          Dispute workflow for unmatched and out-of-tolerance intercompany items. Investigate, propose resolution, and escalate as needed.
        </TextView>
      </div>

      <div className="flex items-center gap-3">
        <TextView variant="label-xs">Period</TextView>
        <Select
          value={selectedPeriodId}
          onValueChange={setSelectedPeriodId}
          options={periods.map((p) => ({ value: String(p.id), label: periodDisplayName(p) }))}
          fullWidth={false}
        />
      </div>

      {error && (
        <div className="card p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
          <p className="text-sm text-amber-700">{error}</p>
        </div>
      )}

      <Card>
        {isLoading ? (
          <div className="p-6 flex items-center justify-center text-gray-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Loading disputes...
          </div>
        ) : disputes.length === 0 ? (
          <div className="py-16 text-center">
            <FileBarChart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="text-sm font-medium text-gray-900">No IC disputes</h3>
            <p className="text-sm text-gray-500 mt-1">
              Unmatched or out-of-tolerance items from the matching engine will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header text-left">Entity Pair</th>
                  <th className="table-header text-right">Amount From</th>
                  <th className="table-header text-right">Amount To</th>
                  <th className="table-header text-left">Currency</th>
                  <th className="table-header text-left">Type</th>
                  <th className="table-header text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {disputes.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50/50">
                    <td className="table-cell">
                      {d.entity_from_name} ↔ {d.entity_to_name}
                    </td>
                    <td className="table-cell text-right font-mono">{Number(d.amount_from).toLocaleString()}</td>
                    <td className="table-cell text-right font-mono">{Number(d.amount_to).toLocaleString()}</td>
                    <td className="table-cell">{d.currency}</td>
                    <td className="table-cell">{d.transaction_type}</td>
                    <td className="table-cell capitalize">{d.status.replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
